// brain.ts — the AI client. Calls the LIVE Oriora server brain for this app
//   https://api.orioralabs.com/api/apps/solo_habit_tracker/analyze
// with the customer's Oriora session token. The server holds the prompt + bills the
// Oriora wallet (Managed AI / BYOK). Habit data stays local in SQLite; only the
// DERIVED SIGNALS object (habit-signals/v2) is sent — small per-habit aggregates
// (adherence %, streak/gap details, cadence-target-vs-observed, habit maturity,
// this-month-vs-last), portfolio reads (active streaks, dormant count, this-week-vs-
// last-week), and a pre-ranked `headline_facts[]` of ready-to-say observational
// sentences computed deterministically on-device. Raw check-in history NEVER leaves
// the device. Everything is OBSERVATIONAL — patterns/deltas, never advice.

import { invoke } from "./tauri-web";
import { getAccessToken } from "./supabase";
import type { AnalysisSignals } from "./store";

const SERVER = "https://api.orioralabs.com";

export type BrainErrorCode = "insufficient_funds" | "managed_ai_blocked" | "unauthenticated" | "unavailable" | "no_data" | "no_store" | "too_large";
export class BrainError extends Error {
  code: BrainErrorCode;
  constructor(code: BrainErrorCode, message: string) {
    super(message);
    this.code = code;
    this.name = "BrainError";
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// SSE reader for the streaming fleet chat (request body {stream:true} → text/event-stream):
// `: open` / `: keepalive` comments and [DONE] are ignored, each `data: {"chunk":…}` event
// feeds onChunk, and the `data: {"done":true, …}` event carries the exact old JSON payload —
// returned where res.json() used to be, so all existing action/reply mapping stays untouched.
async function readSseChat(bodyStream: ReadableStream<Uint8Array>, onChunk: (t: string) => void): Promise<Record<string, unknown>> {
  const reader = bodyStream.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let done: Record<string, unknown> | null = null;
  for (;;) {
    const { value, done: eof } = await reader.read();
    if (eof) break;
    buf += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf("\n")) !== -1) {
      const line = buf.slice(0, nl).replace(/\r$/, "");
      buf = buf.slice(nl + 1);
      if (!line.startsWith("data:")) continue; // comment (": open"/": keepalive") or blank line
      const payload = line.slice(5).trim();
      if (payload === "[DONE]") continue;
      let ev: Record<string, unknown>;
      try { ev = JSON.parse(payload) as Record<string, unknown>; } catch { continue; }
      if (typeof ev.chunk === "string") onChunk(ev.chunk);
      else if (ev.done) done = ev;
    }
  }
  if (!done) throw new BrainError("unavailable", "The reply stream ended early — please try again.");
  // A streamed reply can end with a billing/auth error instead of a payload ({done:true, error, _status}).
  // Returning it as data dissolved the "Top up" message into an empty action (found live 2026-08-27) —
  // throw the same BrainError the buffered path would.
  if (typeof done.error === "string" && done.error) {
    const st = typeof done._status === "number" ? done._status : 0;
    const msg = typeof done.message === "string" && done.message ? done.message : done.error;
    if (st === 401) throw new BrainError("unauthenticated", msg);
    if (st === 402) throw new BrainError("insufficient_funds", msg);
    if (st === 403) throw new BrainError("managed_ai_blocked", msg);
    throw new BrainError("unavailable", msg);
  }
  return done;
}

export async function brainCall<T>(endpoint: string, body: unknown, onChunk?: (t: string) => void): Promise<T> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  // Retry transient failures (502/503/504 + network). Hard errors (401/402/403) never retry.
  const MAX = 3;
  for (let attempt = 1; attempt <= MAX; attempt++) {
    let res: Response;
    try {
      res = await fetch(`${SERVER}/api/apps/solo_habit_tracker/${endpoint}`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify(onChunk && body && typeof body === "object" ? { ...(body as Record<string, unknown>), stream: true } : body),
      });
    } catch {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is temporarily unavailable.");
    }
    if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
    if (res.status === 402) throw new BrainError("insufficient_funds", "AI is paused — add funds to continue.");
    if (res.status === 403) throw new BrainError("managed_ai_blocked", (await res.json().catch(() => null))?.message || "Connect a vendor key in Oriora settings to use AI for this app.");
    if ([502, 503, 504].includes(res.status)) {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is busy — please try again.");
    }
    if (!res.ok) throw new BrainError("unavailable", "AI request failed.");
    // Streaming fleet chat: with onChunk the body carries stream:true and the server answers
    // as SSE — read it incrementally (readSseChat above); the done event IS the old JSON
    // payload. Anything that is not an event stream (old server, an error status body, a
    // proxy that buffered it away) falls through to the buffered path — never a dead end.
    if (onChunk && res.body && (res.headers.get("content-type") ?? "").includes("text/event-stream")) {
      return (await readSseChat(res.body, onChunk)) as unknown as T;
    }
    return (await res.json()) as T;
  }
  throw new BrainError("unavailable", "AI is temporarily unavailable.");
}

// ── The one AI feature: habit-pattern analysis ───────────────────────────────
// Server contract (oriora-server/src/routes/catalog-apps/solo-habit-tracker.ts):
//   POST /analyze  { signals: AnalysisSignals }   ← the richer derived-signal payload
//   → { analysis: { observations: string[],          // 2-3 specific, number-naming observations
//                   headline?: string,               // one short summary line (optional)
//                   adherence_by_habit?: {name: pct}, // optional bars (we can also derive locally)
//                   raw?: string },                  // plain-text fallback
//       amount_usd, amount_units }
// Backward-tolerant: the server may also return the OLD shape
//   { analysis: { streak_summary, one_insight } } — we still surface those.
// And if the model didn't return clean JSON the server may send { analysis: { raw } }.

export interface HabitAnalysis {
  observations?: string[];               // NEW — the rich, specific, number-naming patterns
  headline?: string;                     // NEW — optional one-line summary
  adherence_by_habit?: Record<string, number>;
  streak_summary?: string;               // legacy — still rendered if present
  one_insight?: string;                  // legacy — still rendered if present
  raw?: string;
}

export interface AnalyzeResult {
  analysis: HabitAnalysis;
  amount_usd: number | null;
  amount_units: number | null;
}

// Derive the adherence bars locally from the signals, so the bar render is always
// populated from on-device math even if the server doesn't echo adherence back.
function localAdherence(signals: AnalysisSignals): Record<string, number> {
  const out: Record<string, number> = {};
  for (const h of signals.habits) {
    if (h.adherence_30d_pct >= 0) out[h.name] = h.adherence_30d_pct;
  }
  return out;
}

export async function analyzeHabits(signals: AnalysisSignals): Promise<AnalyzeResult> {
  // Need at least one check-in somewhere for the analysis to mean anything.
  if (signals.total_checkins_all <= 0) {
    throw new BrainError("no_data", "Check in to a habit first, then I can spot your patterns.");
  }
  const r = await brainCall<{ analysis?: HabitAnalysis; amount_usd?: number; amount_units?: number }>(
    "analyze",
    { signals },
  );
  const analysis = r.analysis ?? {};
  // Always have bars: prefer the server's echo, else derive from local signals.
  if (!analysis.adherence_by_habit || Object.keys(analysis.adherence_by_habit).length === 0) {
    const local = localAdherence(signals);
    if (Object.keys(local).length > 0) analysis.adherence_by_habit = local;
  }
  // A habit being BROKEN has no adherence — its marks are the slips. Drop it from the bars
  // whatever the source said, so a 2-slips-in-a-month customer is never shown a 7% bar beside a
  // 90% one and told their best habit is their worst. (Seen live 2026-08-13.)
  if (analysis.adherence_by_habit) {
    const breaking = new Set(signals.habits.filter((h) => h.polarity === "break").map((h) => h.name));
    for (const name of Object.keys(analysis.adherence_by_habit)) {
      if (breaking.has(name)) delete analysis.adherence_by_habit[name];
    }
    if (Object.keys(analysis.adherence_by_habit).length === 0) delete analysis.adherence_by_habit;
  }
  return {
    analysis,
    amount_usd: typeof r.amount_usd === "number" ? r.amount_usd : null,
    amount_units: typeof r.amount_units === "number" ? r.amount_units : null,
  };
}

// ── Cloud backup (the user owns their data) ─────────────────────────────────────
// The device builds a FAITHFUL backup locally (export_backup); these relay it to the user's OWN
// connected cloud store — their GitHub repo or Google Drive (connected once in the Oriora
// account). Oriora RETAINS nothing. Plain HTTP → works on iOS. Endpoints: the generic
// POST/GET /api/apps/solo_habit_tracker/backup (shared server route).
export interface CloudBackupState { connected: boolean; provider?: string; data?: unknown }

async function authedFetch(endpoint: string, init?: RequestInit): Promise<Response> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  return fetch(`${SERVER}/api/apps/solo_habit_tracker/${endpoint}`, {
    ...init,
    headers: { ...(init?.headers ?? {}), authorization: `Bearer ${token}` },
  });
}

/** Save the local backup envelope to the user's connected cloud store. Throws BrainError('no_store')
 *  when nothing is connected, so the UI can point them at the Connections hub. */
export async function cloudBackupSave(envelope: unknown): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("backup", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ data: envelope }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to back up to the cloud.");
  // 413 = over the standard path's size limit (photo-heavy backup). Not retryable — the caller
  // switches to the device-direct GitHub save, or tells the customer how to enable it.
  if (res.status === 413) throw new BrainError("too_large", "This backup is too large for the standard path.");
  if (!res.ok) throw new BrainError("unavailable", "Cloud backup failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Save arbitrary text — a note the user dictates, or the whole conversation — to the user's OWN
 *  cloud store (their GitHub/Drive). Honors an explicit destination when the user names one. Oriora
 *  retains nothing (text relay only). Throws no_store when nothing is connected. */
export async function cloudStoreSave(
  content: string,
  opts?: { name?: string; kind?: string; destination?: "github" | "gdrive" },
): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("store", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ content, name: opts?.name, kind: opts?.kind, provider: opts?.destination }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to save to the cloud.");
  if (!res.ok) throw new BrainError("unavailable", "Saving to your cloud failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Read the cloud backup for restore. `connected:false` when no store is connected.
 *  Pass `provider` to read a NAMED source (the RESTORE STORY's explicit restore) — the server
 *  409s rather than silently reading a different cloud than the one the customer asked for. */
export async function cloudBackupLoad(provider?: "github" | "gdrive"): Promise<CloudBackupState> {
  let res: Response;
  try { res = await authedFetch(provider ? `backup?provider=${provider}` : "backup"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", `${provider === "gdrive" ? "Google Drive" : "GitHub"} isn't connected in your Oriora account.`);
  if (!res.ok) throw new BrainError("unavailable", "Couldn't read your cloud backup.");
  return (await res.json()) as CloudBackupState;
}

/** The RESTORE STORY status line — which clouds are connected, which one backups WRITE to, and the
 *  dated backup each holds. Ground truth from the relay (CUSTOMER-CLOUD-STORAGE.md §THE RESTORE STORY). */
export interface CloudBackupSlot { backup_exists: boolean; updated_at: string | null; bytes: number | null }
export interface CloudBackupStatusInfo {
  connected: ("github" | "gdrive")[];
  writes_to: "github" | "gdrive" | null;
  slots: Partial<Record<"github" | "gdrive", CloudBackupSlot>>;
}
export async function cloudBackupStatus(): Promise<CloudBackupStatusInfo> {
  let res: Response;
  try { res = await authedFetch("backup/status"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (!res.ok) throw new BrainError("unavailable", "Couldn't check your backup status.");
  return (await res.json()) as CloudBackupStatusInfo;
}

// ── Account (native Oriora session) ───────────────────────────────────────────
// Login defers to the ecosystem web login; session returns via the deep link (App.tsx).
// Web callback returns solo-habit-tracker://auth#access_token=…&refresh_token=…
// Money / balance / billing all live in the Oriora account on the website — never here
// (native-surface rule: an App carries no money or account chrome).
export const openWebLogin = () =>
  invoke<void>("open_external", { url: "https://orioralabs.com/login?return=solo-habit-tracker" });
